// /invite — Twee tabs:
//  1. Ambassadeur uitnodigen → maakt ambassador + AMB-XXXX kortingscode + mailt redeem link
//  2. Checkout-link generator → handmatig pre-filled checkout URL voor publiek (delen via email/whatsapp)

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listAmbassadors, inviteAmbassador, listTicketTypes, type AmbassadorRow as AmbRow } from "@/lib/api";
import { useCurrentEvent } from "@/lib/event-context";
import { fmtDateTime, fmtEuro } from "@/lib/formatters";

const PUBLIC_BASE = (import.meta.env.VITE_BASE_URL as string | undefined) ?? "https://www.odeaandeharing.nl";

type Tab = "ambassador" | "link";

export function InviteIndex() {
  const [tab, setTab] = useState<Tab>("ambassador");

  return (
    <>
      <div className="tabs" style={{ marginBottom: "1.5rem" }}>
        <button className={`tab ${tab === "ambassador" ? "active" : ""}`} onClick={() => setTab("ambassador")}>
          ★ Ambassadeur uitnodigen
        </button>
        <button className={`tab ${tab === "link" ? "active" : ""}`} onClick={() => setTab("link")}>
          🔗 Pre-filled checkout-link
        </button>
      </div>

      {tab === "ambassador" ? <AmbassadorTab /> : <LinkTab />}
    </>
  );
}

function AmbassadorTab() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [result, setResult] = useState<{
    name: string;
    email: string;
    code: string;
    redeem_url: string;
    email_sent: boolean;
    email_error: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: ambassadors } = useQuery({
    queryKey: ["ambassadors"],
    queryFn: () => listAmbassadors(),
  });

  const inviteMut = useMutation({
    mutationFn: () => inviteAmbassador({
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim() || undefined,
      message: message.trim() || undefined,
      send_email: sendEmail,
    }),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      setName(""); setEmail(""); setPhone(""); setMessage("");
      qc.invalidateQueries({ queryKey: ["ambassadors"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div>
      <div className="panel" style={{ marginBottom: "1.2rem" }}>
        <div className="panel-h"><h3>Nieuwe ambassadeur</h3></div>

        <div className="form-grid">
          <div className="field"><label>Naam *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field"><label>Email *</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
        </div>
        <div className="field"><label>Telefoon</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="field">
          <label>Persoonlijk bericht (optioneel — wordt nog niet in template gebruikt)</label>
          <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>
        <div className="field">
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
            Direct welkom-email versturen via Resend
          </label>
        </div>

        {error && (
          <div style={{ background: "rgba(255,80,80,0.12)", border: "1px solid rgba(255,80,80,0.4)", padding: "0.7rem", borderRadius: 6, marginBottom: "0.7rem", fontSize: "0.85rem" }}>
            ⚠ {error}
          </div>
        )}

        <button
          className="btn-admin"
          disabled={inviteMut.isPending || !name.trim() || !email.trim()}
          onClick={() => inviteMut.mutate()}
        >
          {inviteMut.isPending ? "Bezig…" : "→ Maak ambassadeur + verstuur"}
        </button>

        {result && (
          <div style={{ marginTop: "1.4rem", background: "rgba(255,122,26,0.08)", border: "1px solid rgba(255,122,26,0.3)", padding: "1rem", borderRadius: 6 }}>
            <strong>✓ Aangemaakt — {result.name}</strong>
            <div style={{ marginTop: 8, fontFamily: "var(--f-mono)", fontSize: "0.85rem", lineHeight: 1.7 }}>
              <div>Korting­code: <code style={{ color: "var(--c-orange)" }}>{result.code}</code></div>
              <div>Email: {result.email}</div>
              <div style={{ marginTop: 6 }}>
                Redeem-link:
                <br />
                <CopyLink url={result.redeem_url} />
              </div>
              <div style={{ marginTop: 6 }}>
                Welkom-email: {result.email_sent ? "✓ verzonden" : `✕ niet verzonden${result.email_error ? ` (${result.email_error})` : ""}`}
              </div>
            </div>
          </div>
        )}
      </div>

      <AmbassadorsList ambassadors={ambassadors ?? []} />
    </div>
  );
}

function AmbassadorsList({ ambassadors }: { ambassadors: AmbRow[] }) {
  // Sales-funnel KPI's
  const total = ambassadors.length;
  const withOwn = ambassadors.filter((a) => a.own_order_status === "paid" || a.own_order_status === "invoice_paid").length;
  const totalRedemptions = ambassadors.reduce((s, a) => s + (a.redeemed_extra_tickets ?? 0), 0);
  const totalRevenue = ambassadors.reduce((s, a) => s + (a.redeemed_extra_revenue_cents ?? 0), 0);

  return (
    <div>
      <div className="kpis" style={{ marginBottom: "1rem" }}>
        <div className="kpi">
          <div className="l">Ambassadeurs</div>
          <div className="v">{total}</div>
          <div className="delta">Uitgenodigd via /invite</div>
        </div>
        <div className="kpi">
          <div className="l">Ambassadeur betaald</div>
          <div className="v">{withOwn} / {total}</div>
          <div className="delta">Ambassadeurschap betaald</div>
        </div>
        <div className="kpi">
          <div className="l">Korting gebruikt</div>
          <div className="v">{totalRedemptions}</div>
          <div className="delta">Vrienden via AMB-code</div>
        </div>
        <div className="kpi">
          <div className="l">Omzet via codes</div>
          <div className="v">{fmtEuro(totalRevenue)}</div>
          <div className="delta">Orders met AMB-code</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-h">
          <h3>Ambassadeurs-lijst</h3>
        </div>
        {ambassadors.length === 0 ? (
          <div className="empty">Nog geen ambassadeurs. Maak er één aan via het formulier links.</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>Code</th>
                  <th>Eigen ticket</th>
                  <th>Vrienden via code</th>
                  <th>Aangemaakt</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {ambassadors.map((a) => {
                  const ownPaid = a.own_order_status === "paid" || a.own_order_status === "invoice_paid";
                  const ownPending = a.own_order_status === "pending" || a.own_order_status === "draft";
                  const redeemUrl = a.redeem_token
                    ? `https://www.odeaandeharing.nl/checkout.html?flow=ambassador&amb=${a.redeem_token}`
                    : null;
                  return (
                    <tr key={a.id}>
                      <td>
                        <strong>{a.name}</strong>
                        <br /><small>{a.email}</small>
                        {a.phone && <><br /><small style={{ opacity: 0.6 }}>{a.phone}</small></>}
                      </td>
                      <td>
                        <code style={{ color: "var(--c-orange)", fontFamily: "var(--f-mono)" }}>
                          {a.discount_code?.code ?? "—"}
                        </code>
                        {a.discount_code && (
                          <><br /><small style={{ opacity: 0.6 }}>
                            {a.discount_code.used_count}
                            {a.discount_code.max_uses ? ` / ${a.discount_code.max_uses}` : ""}
                            {a.discount_code.max_tickets_per_redemption ? " tickets" : " keer"}
                          </small></>
                        )}
                      </td>
                      <td>
                        {ownPaid ? (
                          <Link to={`/orders/${a.own_order_id}`} className="pill-st paid" style={{ textDecoration: "none" }}>
                            ✓ Betaald
                          </Link>
                        ) : ownPending ? (
                          <Link to={`/orders/${a.own_order_id}`} className="pill-st pending" style={{ textDecoration: "none" }}>
                            ⏳ Open
                          </Link>
                        ) : (
                          <span style={{ opacity: 0.5, fontSize: "0.78rem" }}>Nog niet</span>
                        )}
                        {a.own_order_no && <><br /><small style={{ opacity: 0.6 }}>{a.own_order_no}</small></>}
                      </td>
                      <td>
                        <strong>{a.redeemed_extra_tickets ?? 0}×</strong>
                        {(a.redeemed_extra_revenue_cents ?? 0) > 0 && (
                          <><br /><small style={{ opacity: 0.6 }}>{fmtEuro(a.redeemed_extra_revenue_cents ?? 0)}</small></>
                        )}
                      </td>
                      <td><small>{fmtDateTime(a.created_at)}</small></td>
                      <td>
                        {redeemUrl && (
                          <button
                            className="btn-mini"
                            onClick={() => {
                              navigator.clipboard.writeText(redeemUrl);
                              alert("Link gekopieerd");
                            }}
                          >
                            📋 Link
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function LinkTab() {
  const { data: event } = useCurrentEvent();
  const { data: ticketTypes } = useQuery({
    queryKey: ["ticket_types", event?.id],
    queryFn: () => listTicketTypes(event!.id),
    enabled: !!event?.id,
  });

  const [flow, setFlow] = useState<"regular" | "ambassador" | "sponsor">("regular");
  const [items, setItems] = useState<Record<string, number>>({});
  const [discount, setDiscount] = useState("");
  const [email, setEmail] = useState("");

  const eligible = (ticketTypes ?? []).filter((t) => {
    if (flow === "regular") return t.code === "regular";
    if (flow === "sponsor") return t.flow === "sponsor";
    return t.code === "ambassador" || t.code === "regular";
  });

  // Build URL: public site checkout.html ?flow=...&items=tt1:2,tt2:1&code=AMB-...&email=...
  const url = (() => {
    const params = new URLSearchParams();
    params.set("flow", flow);
    const itemsStr = Object.entries(items)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const tt = ticketTypes?.find((t) => t.id === id);
        return tt ? `${tt.code}:${qty}` : null;
      })
      .filter(Boolean)
      .join(",");
    if (itemsStr) params.set("items", itemsStr);
    if (discount.trim()) params.set("code", discount.trim().toUpperCase());
    if (email.trim()) params.set("email", email.trim().toLowerCase());
    return `${PUBLIC_BASE}/checkout.html?${params.toString()}`;
  })();

  const subtotal = (() => {
    if (!ticketTypes) return 0;
    let s = 0;
    for (const [id, qty] of Object.entries(items)) {
      const tt = ticketTypes.find((t) => t.id === id);
      if (tt) s += tt.price_cents * qty;
    }
    return s;
  })();

  return (
    <div className="row2">
      <div className="panel">
        <div className="panel-h"><h3>Checkout-link bouwer</h3></div>
        <p style={{ fontSize: "0.8rem", opacity: 0.7, marginBottom: "1rem" }}>
          Stuur deze URL naar iemand persoonlijk (whatsapp / DM / email) — checkout-pagina is voor­gevuld.
        </p>

        <div className="field">
          <label>Flow</label>
          <select value={flow} onChange={(e) => setFlow(e.target.value as typeof flow)}>
            <option value="regular">Regulier</option>
            <option value="ambassador">Ambassadeur</option>
            <option value="sponsor">Sponsor</option>
          </select>
        </div>

        <div className="field">
          <label>Items</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {eligible.map((tt) => (
              <div key={tt.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "0.7rem", alignItems: "center" }}>
                <div>
                  <strong>{tt.name}</strong> <small style={{ opacity: 0.6 }}>· {tt.code}</small>
                </div>
                <span style={{ color: "var(--c-orange)", fontFamily: "var(--f-mono)" }}>{fmtEuro(tt.price_cents)}</span>
                <input
                  type="number"
                  min={0}
                  max={tt.max_per_order ?? 999}
                  value={items[tt.id] ?? 0}
                  onChange={(e) => {
                    const v = Math.max(0, Number(e.target.value));
                    setItems((p) => {
                      const n = { ...p };
                      if (v === 0) delete n[tt.id]; else n[tt.id] = v;
                      return n;
                    });
                  }}
                  style={{ width: 70 }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-grid">
          <div className="field">
            <label>Korting­code (optioneel)</label>
            <input value={discount} onChange={(e) => setDiscount(e.target.value.toUpperCase())} />
          </div>
          <div className="field">
            <label>Email pre-fill (optioneel)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: "0.5rem", fontFamily: "var(--f-mono)", textAlign: "right" }}>
          Subtotaal: <strong style={{ color: "var(--c-orange)" }}>{fmtEuro(subtotal)}</strong>
        </div>
      </div>

      <div className="panel">
        <div className="panel-h"><h3>Resultaat</h3></div>
        {Object.keys(items).length === 0 ? (
          <div className="empty">Selecteer eerst items om de link te genereren.</div>
        ) : (
          <>
            <div className="field">
              <label>Gegenereerde URL</label>
              <CopyLink url={url} />
            </div>

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.7rem" }}>
              <a className="btn-admin sm" href={url} target="_blank" rel="noopener">↗ Open</a>
              <a
                className="btn-admin ghost sm"
                href={`https://wa.me/?text=${encodeURIComponent(`Hoi! Hier je persoonlijke checkout-link voor de Haringparty 2026: ${url}`)}`}
                target="_blank"
                rel="noopener"
              >
                💬 Deel via WhatsApp
              </a>
              <a
                className="btn-admin ghost sm"
                href={`mailto:${email || ""}?subject=Haringparty 2026 — je checkout-link&body=${encodeURIComponent(`Hoi,\n\nHier je persoonlijke checkout-link voor de Haringparty 2026:\n\n${url}\n\nGroet`)}`}
              >
                ✉ Mail-client openen
              </a>
            </div>

            <div style={{ marginTop: "1rem", fontSize: "0.72rem", opacity: 0.55, lineHeight: 1.6 }}>
              <strong>Let op:</strong> de publieke checkout.html moet deze query-params lezen (flow, items, code, email).
              Als de pre-fill nog niet werkt op de live site, herinner ons om dat in een aparte iteratie toe te voegen.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
      <input
        readOnly
        value={url}
        style={{ flex: 1, fontFamily: "var(--f-mono)", fontSize: "0.78rem" }}
        onFocus={(e) => e.currentTarget.select()}
      />
      <button
        type="button"
        className="btn-admin sm"
        onClick={() => {
          navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "✓ Gekopieerd" : "📋 Kopieer"}
      </button>
    </div>
  );
}
